import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import LiveSessionEntryButton from '../liveSession/components/LiveSessionEntryButton';
import { useStrategistLiveEntry } from '../liveSession/hooks/useStrategistLiveEntry';
import { useMyTeam } from '../team/useMyTeam';
import Sidebar from './Sidebar';
import styles from './DashboardPage.module.css';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { team } = useMyTeam(user?.id);
  const liveEntry = useStrategistLiveEntry(team?.id);
  const [signingOut, setSigningOut] = useState(false);

  const initials = user ? buildInitials(user.displayName || user.email) : '??';
  const teamName = team?.name?.trim() ? team.name : null;
  const teamInitials = teamName ? buildTeamInitials(teamName) : team ? '??' : null;

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className={styles.app}>
      <Sidebar />

      <div className={styles.main}>
        <header className={styles.topbar}>
          {team && (
            <>
              <div className={styles.teamCtx}>
                <span className={styles.teamCtxLogo}>{teamInitials}</span>
                <div className={styles.teamCtxInfo}>
                  <div className={styles.teamCtxLabel}>Active Team</div>
                  <div className={styles.teamCtxName}>{teamName ?? 'Unnamed team'}</div>
                </div>
              </div>
              <div className={styles.topbarDivider} aria-hidden="true" />
            </>
          )}

          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <span>Stratos</span>
            <span className={styles.sep}>/</span>
            <span className={styles.here}>Dashboard</span>
          </nav>

          <div className={styles.spacer} />

          <LiveSessionEntryButton entry={liveEntry} />

          {user && (
            <div className={styles.user}>
              <span className={styles.avatar} aria-hidden="true">
                {initials}
              </span>
              <div>
                <div className={styles.userName}>{user.displayName}</div>
                <div className={styles.userEmail}>{user.email}</div>
              </div>
            </div>
          )}

          <button type="button" className={styles.signOut} onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </header>

        <section className={styles.content}>
          <div className={styles.pageHead}>
            <div className={styles.eyebrow}>Overview</div>
            <h1 className={styles.pageTitle}>
              {user ? `Welcome back, ${firstName(user.displayName)}.` : 'Welcome back.'}
            </h1>
            <p className={styles.pageSubtitle}>
              This is the starting point for your team's playbook. Strategies, learning progress, and live round execution will live here
              as each module comes online.
            </p>
          </div>

          <div className={styles.grid}>
            <article className={styles.card}>
              <div className={styles.cardLabel}>Strategies</div>
              <h2 className={styles.cardTitle}>Build &amp; review playbooks</h2>
              <p className={styles.cardBody}>
                Strategists will draft round-by-round plans here — role assignments, buy profiles, and utility timelines per map.
              </p>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>Learn</div>
              <h2 className={styles.cardTitle}>Track role mastery</h2>
              <p className={styles.cardBody}>
                Players will mark roles as learned and see what's still outstanding from their priority list.
              </p>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>Live</div>
              <h2 className={styles.cardTitle}>Run the chosen strat</h2>
              <p className={styles.cardBody}>
                In-match view that highlights the active action per player as the round clock progresses.
              </p>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>Analyse</div>
              <h2 className={styles.cardTitle}>Review strategy performance</h2>
              <p className={styles.cardBody}>
                Break down how executed strategies played out — win rates by map and side, where rounds broke down, and which calls are worth
                keeping in the playbook.
              </p>
            </article>
          </div>

          <div className={styles.placeholder}>
            <h3 className={styles.placeholderTitle}>More coming soon.</h3>
            <p className={styles.placeholderBody}>
              The dashboard widgets are stubs — strategy lists, live relay status, and learning progress land once the backend endpoints are wired up.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function buildInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstName(displayName: string | null | undefined): string {
  const value = (displayName ?? '').trim();
  if (!value) return 'there';
  return value.split(/\s+/)[0] || value;
}

function buildTeamInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts
    .slice(0, 3)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}
